'use client';

import { useEffect, useState } from 'react';

/**
 * "Back to top" — the one control on the page that is about the page itself.
 *
 * ── Why it exists ────────────────────────────────────────────────────────────
 *
 * An answer can be far taller than the window: paste three paragraphs and the
 * translation, its notes and the overview cards push the mode column, the
 * language bar and the input box off the top of the screen. Everything a reader
 * wants NEXT lives up there — a different mode, the other language, editing
 * what they typed — so the way back is a scroll through the very text they just
 * finished reading. This is the shortcut, and it is one button for the whole
 * page rather than one per pane: the page has a single top.
 *
 * ── Why it is only sometimes there ───────────────────────────────────────────
 *
 * Two conditions, and both of them matter:
 *
 *   • the page is genuinely scrollable. `body { overflow-y: scroll }` in
 *     globals.css keeps a scrollbar gutter on every page whether or not there
 *     is anything to scroll — see the note there — so the scrollbar itself
 *     cannot be the signal. The document's height against the window's is.
 *
 *   • and the top is out of view. A "back to top" offered while you are already
 *     at the top is a button that cannot do anything.
 *
 * ── Why it watches the body, not only the scroll ─────────────────────────────
 *
 * The page GROWS while a translation streams in, so the moment it becomes
 * scrollable is not a scroll, not a resize, and not anything else the window
 * reports. A ResizeObserver on the body is what actually catches it; without
 * one the button stays hidden through the whole first answer and only appears
 * when the reader scrolls — which is the moment they no longer needed telling.
 */

/** Under this much overflow the page is not meaningfully scrollable. */
const SCROLLABLE_SLACK = 120;

/** How far down before it appears — about "the header has gone". */
const SHOW_AFTER = 280;

export default function ScrollTopButton() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const check = () => {
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - window.innerHeight > SCROLLABLE_SLACK;
      setShow(scrollable && window.scrollY > SHOW_AFTER);
    };

    check();
    window.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check);
    const grow = new ResizeObserver(check);
    grow.observe(document.body);

    return () => {
      window.removeEventListener('scroll', check);
      window.removeEventListener('resize', check);
      grow.disconnect();
    };
  }, []);

  const toTop = () => {
    // Smooth, unless the reader has asked their system for less motion. A page
    // tall enough to earn this button animates for a long way, which is exactly
    // the case that preference exists for.
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });
  };

  return (
    <button
      type="button"
      onClick={toTop}
      title="Back to top"
      aria-label="Back to top"
      // Faded out rather than unmounted, so it arrives and leaves instead of
      // blinking. A control mid-fade must not still be clickable or reachable
      // by Tab, hence the other two.
      aria-hidden={!show}
      tabIndex={show ? 0 : -1}
      className={
        // z-30 places it above the page (which tops out at z-20) and BELOW the
        // page bar at z-40 — those two share the bottom of the window, and on a
        // narrow screen the centred bar can reach this corner. Letting the bar
        // win is right: it is the control with somewhere to go.
        'fixed bottom-4 right-4 z-30 flex h-11 w-11 items-center justify-center rounded-full ' +
        'border border-ink-200 bg-white/95 text-ink-600 shadow-xl backdrop-blur ' +
        'transition duration-200 hover:bg-ink-100 hover:text-ink-900 ' +
        'focus-visible:ring-2 focus-visible:ring-accent-500 focus-visible:outline-none ' +
        'dark:border-ink-700 dark:bg-ink-800/95 dark:text-ink-300 ' +
        'dark:hover:bg-ink-700 dark:hover:text-white ' +
        (show ? 'opacity-100' : 'pointer-events-none translate-y-2 opacity-0')
      }
    >
      <svg
        viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      >
        <path d="M12 19V5" />
        <path d="M5 12l7-7 7 7" />
      </svg>
    </button>
  );
}
